import React, { useState, useEffect, useRef, useContext } from 'react';
import {
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  X,
  ArrowLeft,
  Phone,
  Mail,
  ChevronRight,
  AlertCircle,
  Pencil,
  Trash2,
} from 'lucide-react';
import DashboardLayout from '../../components/DashboardLayout';
import {
  profileApiService,
  UpdatePersonalInfoRequest,
  UpdatePasswordRequest,
} from '../../services/profileApi';
import { useAuth } from '../../hooks/useAuth';
import '../../styles/components/settings.css';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { aqiStandardApi, AqiStandard, ParametersWithPreferredUnit, updateParameterUnitsPreferencesRequest } from '../../services/aqiStandardApi';
import { getAllTimezones } from '../../utils/timezoneUtils';
import SearchableSelect from '../../components/SearchableSelect';
import { DevicesContext } from '../../contexts/DevicesContext';
import { UserSelectedContext } from '../../contexts/UserSelectedContext';
import LogoutConfirmationModal from '../../components/modals/LogoutConfirmationModal';
import AccountDeletionModal from '../../components/modals/AccountDeletionModal';
import ChangeEmailModal from '../../components/modals/ChangeEmailModal';
import { apiService } from '../../services/api';
import { isPhoneEmpty, isValidE164, e164ToBackendFormat, toE164 } from '../../utils/helperFunctions';
import { validateEmail } from '../../utils/emailValidation';
import PhoneNumberInput from '../../components/PhoneNumberInput';

const accountSchema = yup.object().shape({
  FirstName: yup
    .string()
    .required('First name is required')
    .test('trimmed-length', 'First name must be at least 4 characters', value => !value || value.trim().length >= 4)
    .test('max-length', 'First name cannot exceed 25 characters', value => !value || value.length <= 25)
    .test('letters-only', 'First name can only contain letters and spaces', value => !value || /^[a-zA-Z\s]+$/.test(value))
    .test('not-only-spaces', 'First name cannot be only spaces', value => !value || value.trim().length > 0)
    .test('not-all-same', 'Enter a valid first name', value => !value || !/^([a-zA-Z])\1+$/.test(value.trim()))
    .test('no-repeated-chars', 'First name cannot have a character repeated more than twice in a row', value => !value || !/([a-zA-Z])\1{2,}/.test(value))
    .test('no-gibberish', 'Enter a valid first name', value => !value || !/[^aeiou\s]{5,}/i.test(value.trim())),
  LastName: yup
    .string()
    .required('Last name is required')
    .test('min-length', 'Last name must be at least 2 characters', value => !value || value.length >= 2)
    .test('max-length', 'Last name cannot exceed 25 characters', value => !value || value.length <= 25)
    .test('letters-only', 'Last name can only contain letters', value => !value || /^[a-zA-Z]+$/.test(value))
    .test('not-all-same', 'Enter a valid last name', value => !value || !/^([a-zA-Z])\1+$/.test(value.trim()))
    .test('no-repeated-chars', 'Last name cannot have a character repeated more than twice in a row', value => !value || !/([a-zA-Z])\1{2,}/.test(value))
    .test('no-gibberish', 'Enter a valid last name', value => !value || !/[^aeiou\s]{5,}/i.test(value.trim())),
  Email: yup
    .string()
    .test('email-validation', '', function (value) {
      // Hard-block on Settings: format + disposable. Email changes go
      // through ChangeEmailModal; this guards any save that would persist
      // the displayed value (e.g. legacy paths that pass through this form).
      const result = validateEmail(value, { blockDisposable: true, checkTypo: false });
      if (result.ok) return true;
      return this.createError({ message: result.error });
    }),
  PhoneNumber: yup
    .string()
    .test('required', 'Mobile number is required', value => !isPhoneEmpty(value))
    .test('valid', 'Please enter a valid mobile number for the selected country', value => isPhoneEmpty(value) || isValidE164(value)),
});

/* Mobile Profile Screen Props */
type MobileProfileScreenProps = {
  profilePictureUrl: string;
  firstName: string;
  phoneNumber: string;
  email: string;
  setMobileScreen: (_screen: 'profile' | 'account' | 'password' | 'preferences' | 'interface') => void;
  setShowLogoutModal: (_show: boolean) => void;
  hideBottomBar: () => void;
  theme: string;
};

  // Mobile Profile Screen Component
  const MobileProfileScreen = ({ profilePictureUrl, firstName, phoneNumber, email, setMobileScreen, setShowLogoutModal, hideBottomBar, theme }: MobileProfileScreenProps) => (
    <div className="mobile-screen mobile-profile-screen">
      <div className="mobile-header border-b border-[var(--gradient-page-bottom-color)]">
        <div className="mobile-profile-photo">
          {profilePictureUrl ? (
            <img
              src={profilePictureUrl}
              alt="Profile"
              className="mobile-profile-image"
            />
          ) : (
            <div className='mobile-profile-image'>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill= {theme == "light" ? '#363636' : '#fff'}>
                <path d="M320 312C386.3 312 440 258.3 440 192C440 125.7 386.3 72 320 72C253.7 72 200 125.7 200 192C200 258.3 253.7 312 320 312zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z" />
              </svg>
            </div>
          )}
        </div>
        <div className="mobile-user-details">
          <h6 className="mobile-user-name">{firstName || 'N/A'}</h6>
          <div className="mobile-contact-info">
            {phoneNumber && phoneNumber.replace(/\D/g, '').length > 4 && (
              <div className="mobile-contact-item">
                <Phone className="mobile-contact-icon" />
                <span>{phoneNumber}</span>
              </div>
            )}
            <div className="mobile-contact-item">
              <Mail className="mobile-contact-icon" />
              <span>{email}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="mobile-content">
        <div className="mobile-menu">
          <button
            className="mobile-menu-item"
            onClick={() => {setMobileScreen('account'); hideBottomBar();}}
          >
            <div className="mobile-menu-icon-container">
              <img
                src={`${process.env.PUBLIC_URL}/SettingsPageIcons/SmallUserIcon.svg`}
                alt="Account Settings"
                className="mobile-menu-icon"
              />
            </div>
            <span>Account Settings</span>
            <ChevronRight className="mobile-menu-arrow" />
          </button>

          <button
            className="mobile-menu-item"
            onClick={() => {setMobileScreen('password'); hideBottomBar();}}
          >
            <div className="mobile-menu-icon-container">
              <img
                src={`${process.env.PUBLIC_URL}/SettingsPageIcons/PasswordIcon.svg`}
                alt="Password & Security"
                className="mobile-menu-icon"
              />
            </div>
            <span>Password & Security</span>
            <ChevronRight className="mobile-menu-arrow" />
          </button>

          <button 
            className="mobile-menu-item"
            onClick={() => {setMobileScreen('preferences'); hideBottomBar();}}
          >
            <div className="mobile-menu-icon-container">
              <img
                src={`${process.env.PUBLIC_URL}/SettingsPageIcons/PreferencesIcon.svg`}
                alt="Preferences"
                className="mobile-menu-icon"
              />
            </div>
            <span>Preferences</span>
            <ChevronRight className="mobile-menu-arrow" />
          </button>

          <button 
            className="mobile-menu-item"
            onClick={() => {setMobileScreen('interface'); hideBottomBar();}}
          >
            <div className="mobile-menu-icon-container">
              <img
                src={`${process.env.PUBLIC_URL}/SettingsPageIcons/InterfaceIcon.svg`}
                alt="Interface"
                className="mobile-menu-icon"
              />
            </div>
            <span>Interface</span>
            <ChevronRight className="mobile-menu-arrow" />
          </button>
        
        </div>

        <button
          className="mobile-logout-btn"
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();
            setShowLogoutModal(true);
          }}
          type="button"
        >
          Logout
        </button>
      </div>
    </div>
  );


  type MobileAccountScreenProps = {
    profilePictureUrl: string;
    handleUploadClick: () => void;
    handleDeletePhoto: () => void;
    isUploading: boolean;
    isDeletingPhoto: boolean;
    accountFields: any[];
    handleSavePersonalInfo: () => void;
    resetPersonalInfo: () => void;
    isSaving: boolean;
    setMobileScreen: (_screen: 'profile' | 'account' | 'password' | 'preferences') => void;
    fieldErrors: {[key: string]: string};
    setFieldErrors: React.Dispatch<React.SetStateAction<{[key: string]: string}>>;
    setShowDeleteAccountModal: (_show: boolean) => void;
    theme: string;
    openChangeEmailModal: () => void;
  };

  // Mobile Account Settings Screen Component
  const MobileAccountScreen = ({ profilePictureUrl, handleUploadClick, handleDeletePhoto, isUploading, isDeletingPhoto, accountFields, handleSavePersonalInfo, resetPersonalInfo, isSaving, setMobileScreen, fieldErrors, setFieldErrors, setShowDeleteAccountModal, theme, openChangeEmailModal }: MobileAccountScreenProps) => (
    <div className="mobile-screen mobile-account-screen">
      <div className="mobile-header">
        <button
          className="cursor-pointer"
          onClick={() => {
            resetPersonalInfo();
            setMobileScreen('profile');
          }}
        >
          <ArrowLeft className="mobile-back-icon" />
        </button>
        <h1 className="mobile-title">Account Settings</h1>
      </div>

      <div className="mobile-content">
        <div className="mobile-profile-section">
          <div className="mobile-profile-photo-container">
            {profilePictureUrl ? (
            <img
              src={profilePictureUrl}
              alt="Profile"
              className="mobile-profile-photo-large"
            />
          ) : (
            <div className='mobile-profile-photo-large'>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" fill = {theme == "light" ? '#363636' : '#fff'}>
                <path d="M320 312C386.3 312 440 258.3 440 192C440 125.7 386.3 72 320 72C253.7 72 200 125.7 200 192C200 258.3 253.7 312 320 312zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z" />
              </svg>
            </div>
          )}
          </div>
          <div className="profile-photo-actions profile-photo-actions--mobile">
            <button
              type="button"
              onClick={handleUploadClick}
              disabled={isUploading || isDeletingPhoto}
              aria-label={profilePictureUrl ? 'Change profile picture' : 'Upload profile picture'}
              title={profilePictureUrl ? 'Change photo' : 'Upload photo'}
              className="profile-photo-icon-btn"
            >
              {isUploading ? (
                <div className="h-4 w-4 border-b-2 border-[var(--color-terms-link)] animate-spin rounded-full"></div>
              ) : (
                <Pencil className="w-4 h-4" />
              )}
            </button>
            {profilePictureUrl && (
              <button
                type="button"
                onClick={handleDeletePhoto}
                disabled={isUploading || isDeletingPhoto}
                aria-label="Remove profile picture"
                title="Remove photo"
                className="profile-photo-icon-btn profile-photo-icon-btn--danger"
              >
                {isDeletingPhoto ? (
                  <div className="h-4 w-4 border-b-2 border-[#dc2626] animate-spin rounded-full"></div>
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            )}
          </div>
        </div>

        <div className="mobile-form-section">
          {accountFields.map((field, index) => {
            const fieldName = field.label.replace(' ', '');
            const isPhone = fieldName === 'PhoneNumber';
            const isEmail = fieldName === 'Email';
            return (
            <div key={index} className="form-field">
              <label className="register-field-label settings-field-label" htmlFor={`mobile-account-${fieldName}`}>
                {field.label}
                {field.required && <span className="text-red-500"> *</span>}
              </label>
              {isPhone ? (
                <PhoneNumberInput
                  inputId={`mobile-account-${fieldName}`}
                  inputName="mobileNumber"
                  value={field.value}
                  onChange={field.onChange}
                  defaultCountry="ae"
                  placeholder="Enter mobile number"
                  error={fieldErrors[fieldName]}
                />
              ) : (
                <>
                  <div className="register-input-with-icon" style={isEmail ? { position: 'relative' } : undefined}>
                    <img
                      src={field.icon}
                      alt={field.label}
                      className="settings-input-icon"
                    />
                    <input
                      id={`mobile-account-${fieldName}`}
                      type={field.type}
                      value={field.value}
                      readOnly={isEmail}
                      onChange={async (e) => {
                        if (isEmail) return;
                        try {
                          field.onChange(e.target.value);
                          await accountSchema.validateAt(fieldName, { [fieldName]: e.target.value });
                          setFieldErrors(prev => ({ ...prev, [fieldName]: '' }));
                        } catch (validationError) {
                          setFieldErrors(prev => ({ ...prev, [fieldName]: (validationError as Error).message }));
                        }
                      }}
                      className={`border-0 register-input settings-input focus:border ${isEmail ? 'cursor-not-allowed pr-10' : ''}`}
                    />
                    {isEmail && (
                      <button
                        type="button"
                        onClick={openChangeEmailModal}
                        aria-label="Change email"
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded text-[var(--color-text)]"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {fieldErrors[fieldName] && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <AlertCircle className="h-4 w-4" />
                      {fieldErrors[fieldName]}
                    </p>
                  )}
                </>
              )}
            </div>
          )})}
        </div>

        {/* Delete Account Section */}
        <div className="mobile-delete-account-section">
          <div className="border-t border-gray-200 pt-4 mt-4">
            <h3 className="text-red-600 font-semibold mb-2 text-base">Delete My Account: Attention Required</h3>
            <p className="text-gray-600 text-sm mb-4">
              Once you delete your account, there is no going back. Please be certain.
            </p>
            <button
              className="primary-button"
              style={{ 
                background: 'linear-gradient(to right, #dc2626, #b91c1c)',
                fontWeight: 500,
                minWidth: '140px',
                padding: '0.625rem 2rem'
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowDeleteAccountModal(true);
              }}
              type="button"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="action-buttons">
        <button
          className="secondary-button"
          onClick={() => {
            resetPersonalInfo();
            setMobileScreen('profile');
          }}
        >
          Cancel
        </button>
        <button
          className="primary-button"
          onClick={handleSavePersonalInfo}
          disabled={isSaving || Object.values(fieldErrors).some(error => error !== '')}
        >
          {isSaving ? (
            <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
          ) : (
            'Save'
          )}
        </button>
      </div>

    </div>
  );


  /* Mobile Password Screen Props */
  type MobilePasswordScreenProps = {
    passwordFields: any[];
    newPassword: string;
    confirmPassword: string;
    passwordError: string;
    setPasswordError: (_error: string) => void;
    isUpdatingPassword: boolean;
    isPasswordFormValid: () => boolean;
    handleUpdatePassword: () => void;
    setMobileScreen: (_screen: 'profile' | 'account' | 'password' | 'preferences') => void;
    setNewPassword: (_password: string) => void;
    setConfirmPassword: (_password: string) => void;
    hasPassword: boolean;
  };

  // Mobile Password Screen Component
  const MobilePasswordScreen = ({ passwordFields, newPassword, confirmPassword: _confirmPassword, passwordError, setPasswordError, isUpdatingPassword, isPasswordFormValid, handleUpdatePassword, setMobileScreen, setNewPassword, setConfirmPassword, hasPassword }: MobilePasswordScreenProps) => (
    <div className="mobile-screen mobile-password-screen">
      <div className="mobile-header">
        <button
          className="cursor-pointer"
          onClick={() => {
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
            setMobileScreen('profile');
          }}
        >
          <ArrowLeft className="mobile-back-icon" />
        </button>
        <h1 className="mobile-title">Password & Security</h1>
      </div>

      <div className="mobile-content">
        <div className="mobile-form-section">
          {passwordFields.map((field, index) => (
            <div key={index} className="mb-4">
              <label className="register-field-label settings-field-label">{field.label}<span className="text-red-500"> *</span></label>
              <div className="password-field-container">
                <img
                  src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`}
                  alt="Lock"
                  className="settings-input-icon"
                />
                <input
                  type={field.showPassword ? 'text' : 'password'}
                  value={field.value}
                  onChange={e => field.onChange(e.target.value)}
                  className="border-0 register-input settings-input focus:border"
                  placeholder={field.placeholder}
                />
                <button
                  type="button"
                  onClick={() => field.setShowPassword(!field.showPassword)}
                  className="password-toggle"
                >
                  {field.showPassword ? (
                    <svg width="22" height="15" viewBox="0 0 22 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z" fill="#B5BBC0"/>
                    </svg>
                  ) : (
                    <svg width="22" height="19" viewBox="0 0 22 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z" fill="#B5BBC0"/>
                    </svg>
                  )}
                </button>
              </div>
            </div>
          ))}

          {/* Password Validation - Shown below confirm password */}
          {newPassword && (
            <div className="password-validation-info">
              <div className="password-validation-info-content">
                <div className="password-validation-info-icon">
                  <span className="password-validation-info-icon-text">i</span>
                </div>
                <span className="password-validation-info-text">
                  Password must be at least 8 characters and include uppercase, lowercase, number and special character.
                </span>
              </div>
            </div>
          )}

          {/* Password Error Message */}
          {passwordError && (
            <div className="password-error">
              <div className="password-error-content">
                <div className="password-error-icon">
                  <span className="password-error-icon-text">!</span>
                </div>
                <span className="password-error-text">{passwordError}</span>
                <X
                  className="h-5 w-5 ml-auto text-[#E67E7D] cursor-pointer hover:text-[#C42B2A]"
                  onClick={() => setPasswordError('')}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="action-buttons">
        <button
          className="secondary-button"
          onClick={() => {
            setNewPassword('');
            setConfirmPassword('');
            setPasswordError('');
            setMobileScreen('profile');
          }}
        >
          Cancel
        </button>
        <button
          className="primary-button"
          onClick={handleUpdatePassword}
          disabled={isUpdatingPassword || !isPasswordFormValid()}
        >
          {isUpdatingPassword && (
            <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
          )}
          <span>
            {isUpdatingPassword
              ? (hasPassword ? 'Updating...' : 'Setting...')
              : (hasPassword ? 'Update' : 'Set Password')}
          </span>
        </button>
      </div>
    </div>
  );

  /* Mobile Preferences Screen Props */
  type MobilePreferencesScreenProps = {
    aqiStandardId: number;
    setAqiStandardId: (_id: number) => void;
    aqiStandards: AqiStandard[];
    timezone: string;
    setTimezone: (_tz: string) => void;
    allParameters: ParametersWithPreferredUnit[];
    handleParameterUnitChange: (_e: React.ChangeEvent<HTMLSelectElement>, _parameterId: number) => void;
    getUnitOptions: (_parameterName: string) => string[];
    handleSavePreferences: () => void;
    isSaving: boolean;
    setMobileScreen: (_screen: 'profile' | 'account' | 'password' | 'preferences') => void;
    // showDemoAccount: boolean;
    // setShowDemoAccount: (_value: boolean) => void;
  };

  // Mobile Preferences Screen Component
  const MobilePreferencesScreen = ({
    aqiStandardId,
    setAqiStandardId,
    aqiStandards,
    timezone,
    setTimezone,
    allParameters,
    handleParameterUnitChange,
    getUnitOptions,
    handleSavePreferences,
    isSaving,
    setMobileScreen,
    // showDemoAccount,
    // setShowDemoAccount,
  }: MobilePreferencesScreenProps) => {
    const navigate = useNavigate();
    const userSelectedContext = useContext(UserSelectedContext);
    const showBottomBar = userSelectedContext?.showBottomBar;
    // Modal states for dropdowns
    const [showAqiStandardModal, setShowAqiStandardModal] = useState(false);
    const [showParameterModal, setShowParameterModal] = useState<number | null>(null);
    // const [showDemoAccountModal, setShowDemoAccountModal] = useState(false);

    // Get AQI Standard options - match desktop format: "Description (StandardName)"
    const aqiStandardOptions = aqiStandards.length > 0
      ? aqiStandards.map(s => ({ 
          id: s.id, 
          label: s.description && s.standardName 
            ? `${s.description} (${s.standardName})` 
            : s.description || s.standardName || `Standard ${s.id}` 
        }))
      : [
          { id: 1, label: 'United States (USA)' },
          { id: 2, label: 'United Arab Emirates (UAE)' },
          { id: 3, label: 'India (INDIA)' },
        ];

    // Get current AQI Standard label
    const currentAqiStandard = aqiStandardOptions.find(opt => opt.id === aqiStandardId)?.label || 'United States (USA)';

    // Full IANA timezone list (memoized once per session). Includes search metadata.
    const timezoneOptions = getAllTimezones();

    return (
      <div className="mobile-screen mobile-preferences-screen">
        <div className="mobile-header">
          <button
            className="cursor-pointer"
            onClick={() => {
              setMobileScreen('profile');
            }}
          >
            <ArrowLeft className="mobile-back-icon" />
          </button>
          <h1 className="mobile-title">Preferences</h1>
        </div>

        <div className="mobile-content">
          {/* AQI Standard & Time Zone Section */}
          <div className="mobile-preferences-section">
            <div className="mobile-preferences-section-header">
              <div className="mobile-preferences-section-icon">
                <img
                  src={`${process.env.PUBLIC_URL}/SettingsPageIcons/LocalIcon.svg`}
                  alt="AQI Standard & Time Zone"
                  className="mobile-preferences-icon-img"
                />
              </div>
              <div className="mobile-preferences-section-title-container">
                <h3 className="mobile-preferences-section-title">AQI Standard & Time Zone</h3>
                <p className="mobile-preferences-section-description">
                  Set your preferred AQI standard and time zone for the app
                </p>
              </div>
            </div>

            <div className="mobile-preferences-form-section">
              <div className="mobile-preferences-field">
                <label className="register-field-label settings-field-label">Select your AQI Standard</label>
                <button
                  type="button"
                  onClick={() => setShowAqiStandardModal(true)}
                  className="mobile-preference-select-button select-input"
                >
                  <span>{currentAqiStandard}</span>
                  <ChevronDown className="w-6 h-6 text-[var(--color-text)]" />
                </button>
              </div>

              <div className="mobile-preferences-field">
                <label className="register-field-label settings-field-label">Select your Time Zone</label>
                <SearchableSelect
                  value={timezone}
                  onChange={setTimezone}
                  options={timezoneOptions}
                  placeholder="Select a timezone"
                  searchPlaceholder="Search timezones"
                  modalTitle="Select Time Zone"
                />
              </div>
            </div>
          </div>

          {/* Unit Preferences Section */}
          <div className="mobile-preferences-section">
            <div className="mobile-preferences-section-header">
              <div className="mobile-preferences-section-icon">
                <img
                  src={`${process.env.PUBLIC_URL}/SettingsPageIcons/UnitsIcon.svg`}
                  alt="Unit Preferences"
                  className="mobile-preferences-icon-img"
                />
              </div>
              <div className="mobile-preferences-section-title-container">
                <h3 className="mobile-preferences-section-title">Unit Preferences</h3>
                <p className="mobile-preferences-section-description">
                  Set your preferred units for the parameters
                </p>
              </div>
            </div>

            <div className="mobile-preferences-form-section">
              {allParameters.length > 0 ? (
                allParameters.map((parameter) => {
                  // Special handling for Temperature - use radio buttons
                  if (parameter.parameterName === 'Temperature') {
                    const unitOptions = getUnitOptions(parameter.parameterName);
                    return (
                      <div key={parameter.parameterId} className="mobile-preferences-field">
                        <label className="register-field-label settings-field-label">{parameter.parameterName}</label>
                        <div className="temperature-radio-group">
                          {unitOptions.map(unit => (
                            <label key={unit} className="temperature-radio-option">
                              <input
                                type="radio"
                                name={`mobile-temperature-${parameter.parameterId}`}
                                value={unit}
                                checked={(parameter.preferredUnitName || '°C') === unit}
                                onChange={_e => {
                                  const syntheticEvent = {
                                    target: { value: unit }
                                  } as React.ChangeEvent<HTMLSelectElement>;
                                  handleParameterUnitChange(syntheticEvent, parameter.parameterId);
                                }}
                                className="temperature-radio-input"
                              />
                              <span className="temperature-radio-label">
                                {unit === '°C' ? '°C' : unit === '°F' ? '°F' : unit}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    );
                  }

                  // For other parameters, use dropdown button
                  const unitOptions = getUnitOptions(parameter.parameterName);
                  const currentUnit = parameter.preferredUnitName || '';
                  const currentUnitLabel = unitOptions.includes(currentUnit) 
                    ? (currentUnit === '°C' ? 'Temperature (°C)' : currentUnit === '°F' ? 'Temperature (°F)' : currentUnit)
                    : unitOptions[0] || '';

                  return (
                    <div key={parameter.parameterId} className="mobile-preferences-field">
                      <label className="register-field-label settings-field-label">{parameter.parameterName}</label>
                      <button
                        type="button"
                        onClick={() => setShowParameterModal(parameter.parameterId)}
                        className="mobile-preference-select-button select-input"
                      >
                        <span>{currentUnitLabel}</span>
                        <ChevronDown className="w-6 h-6 text-[var(--color-text)]" />
                      </button>
                    </div>
                  );
                })
              ): (
                <div className="Parameter-Preferences-Empty-State">
                  <p className="Parameter-Preferences-Empty-State-Description">
                    You haven&apos;t onboarded a device yet.{' '}
                    <button
                      type="button"
                      onClick={() => {
                        showBottomBar?.();
                        navigate('/add-device');
                      }}
                      className="text-[var(--color-terms-link)] underline hover:opacity-80"
                    >
                      Onboard your first device
                    </button>{' '}
                    to set unit preferences.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Demo Account Section — hidden per product decision: demo device is
              auto-shown when the user has 0 real devices, so the toggle is no
              longer needed. Uncomment the block below to restore the setting. */}
          {/*
          <div className="mobile-preferences-section">
            <div className="mobile-preferences-section-header">
              <div className="mobile-preferences-section-icon">
                <img
                  src={`${process.env.PUBLIC_URL}/SettingsPageIcons/interfaceIcon.svg`}
                  alt="Demo Account"
                  className="mobile-preferences-icon-img"
                />
              </div>
              <div className="mobile-preferences-section-title-container">
                <h3 className="mobile-preferences-section-title">Demo Account</h3>
                <p className="mobile-preferences-section-description">
                  Choose whether to see the sample demo device. When off, alerts and share links cannot be created until you onboard a device.
                </p>
              </div>
            </div>

            <div className="mobile-preferences-form-section">
              <div className="mobile-preferences-field">
                <label className="register-field-label settings-field-label">Show demo account</label>
                <button
                  type="button"
                  onClick={() => setShowDemoAccountModal(true)}
                  className="mobile-preference-select-button select-input"
                >
                  <span>{showDemoAccount ? 'Yes, show demo account' : 'No, hide demo account'}</span>
                  <ChevronDown className="w-6 h-6 text-[var(--color-text)]" />
                </button>
              </div>
            </div>
          </div>
          */}
        </div>

        {/* Action Buttons fixed at bottom */}
        <div className="action-buttons">
          <button
            className="secondary-button"
            onClick={() => {
              setMobileScreen('profile');
            }}
          >
            Cancel
          </button>
          <button
            className="primary-button"
            onClick={handleSavePreferences}
            disabled={isSaving}
          >
            {isSaving ? (
              <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
            ) : (
              'Save'
            )}
          </button>
        </div>

        {/* AQI Standard Selection Modal */}
        {showAqiStandardModal && (
          <div
            className="mobile-modal-overlay"
            onClick={() => setShowAqiStandardModal(false)}
          >
            <div className="mobile-modal" onClick={e => e.stopPropagation()}>
              <div className="mobile-modal-header">
                <h3 className="mobile-modal-title">Select AQI Standard</h3>
                <X
                  className="w-5 h-5 text-[var(--color-text)]"
                  onClick={() => setShowAqiStandardModal(false)}
                />
              </div>
              <div className="mobile-modal-content">
                {aqiStandardOptions.map(option => (
                  <label key={option.id} className="mobile-modal-option">
                    <input
                      type="radio"
                      name="aqiStandard"
                      value={option.id}
                      checked={aqiStandardId === option.id}
                      onChange={() => {
                        setAqiStandardId(option.id);
                        setShowAqiStandardModal(false);
                      }}
                      className="mobile-modal-radio"
                    />
                    <span className="mobile-modal-label">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Parameter Unit Selection Modals */}
        {showParameterModal !== null && (() => {
          const parameter = allParameters.find(p => p.parameterId === showParameterModal);
          if (!parameter) return null;
          // Skip modal for Temperature as it uses inline radio buttons
          if (parameter.parameterName === 'Temperature') return null;
          
          const unitOptions = getUnitOptions(parameter.parameterName);
          
          return (
            <div
              className="mobile-modal-overlay"
              onClick={() => setShowParameterModal(null)}
            >
              <div className="mobile-modal" onClick={e => e.stopPropagation()}>
                <div className="mobile-modal-header">
                  <h3 className="mobile-modal-title">Select {parameter.parameterName} Unit</h3>
                  <X
                    className="w-5 h-5 text-[var(--color-text)]"
                    onClick={() => setShowParameterModal(null)}
                  />
                </div>
                <div className="mobile-modal-content">
                  {unitOptions.map(unit => {
                    const unitLabel = unit === '°C' ? 'Temperature (°C)' : unit === '°F' ? 'Temperature (°F)' : unit;
                    return (
                      <label key={unit} className="mobile-modal-option">
                        <input
                          type="radio"
                          name={`parameter-${parameter.parameterId}`}
                          value={unit}
                          checked={(parameter.preferredUnitName || '') === unit}
                          onChange={() => {
                            const syntheticEvent = {
                              target: { value: unit }
                            } as React.ChangeEvent<HTMLSelectElement>;
                            handleParameterUnitChange(syntheticEvent, parameter.parameterId);
                            setShowParameterModal(null);
                          }}
                          className="mobile-modal-radio"
                        />
                        <span className="mobile-modal-label">{unitLabel}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })()}

        {/* Demo Account Selection Modal — hidden alongside the trigger button.
            Uncomment to restore. */}
        {/*
        {showDemoAccountModal && (
          <div
            className="mobile-modal-overlay"
            onClick={() => setShowDemoAccountModal(false)}
          >
            <div className="mobile-modal" onClick={e => e.stopPropagation()}>
              <div className="mobile-modal-header">
                <h3 className="mobile-modal-title">Show Demo Account</h3>
                <X
                  className="w-5 h-5 text-[var(--color-text)]"
                  onClick={() => setShowDemoAccountModal(false)}
                />
              </div>
              <div className="mobile-modal-content">
                {[
                  { value: true, label: 'Yes, show demo account' },
                  { value: false, label: 'No, hide demo account' },
                ].map(option => (
                  <label key={String(option.value)} className="mobile-modal-option">
                    <input
                      type="radio"
                      name="demoAccount"
                      value={String(option.value)}
                      checked={showDemoAccount === option.value}
                      onChange={() => {
                        setShowDemoAccount(option.value);
                        setShowDemoAccountModal(false);
                      }}
                      className="mobile-modal-radio"
                    />
                    <span className="mobile-modal-label">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
        */}
      </div>
    );
  };


   /* Mobile interface Screen Props */
  type MobileInterfaceScreenProps = {
    preferredTheme: string;
    setPreferredTheme: (_theme: 'light' | 'dark') => void;
    toggleTheme: () => void;
    setMobileScreen: (_screen: 'profile' | 'account' | 'password' | 'preferences' | 'interface') => void;
  };

  // Mobile interface Screen Component
  const MobileInterfaceScreen = ({toggleTheme, setMobileScreen, preferredTheme, setPreferredTheme}: MobileInterfaceScreenProps) => {

    const [showThemesModal, setShowThemesModal] = useState(false);

    /* Theme options */
    const themeOptions = [
      {value: 'light', label: 'Light Theme'},
      {value: 'dark', label: 'Dark Theme'},
    ]

    return (
      <div className="mobile-screen mobile-preferences-screen">
        <div className="mobile-header">
          <button
            className="cursor-pointer"
            onClick={() => {
              setMobileScreen('profile');
            }}
          >
            <ArrowLeft className="mobile-back-icon" />
          </button>
          <h1 className="mobile-title">Interface</h1>
        </div>

        <div className="mobile-content">
          {/* AQI Standard & Time Zone Section */}
          <div className="mobile-preferences-section">
            <div className="mobile-preferences-section-header">
              <div className="mobile-preferences-section-icon">
                <img
                  src={`${process.env.PUBLIC_URL}/SettingsPageIcons/LocalIcon.svg`}
                  alt="AQI Standard & Time Zone"
                  className="mobile-preferences-icon-img"
                />
              </div>
              <div className="mobile-preferences-section-title-container">
                <h3 className="mobile-preferences-section-title">Interface</h3>
                <p className="mobile-preferences-section-description">
                  Adjust the appearance and layout of the application interface.
                </p>
              </div>
            </div>

            <div className="mobile-preferences-form-section">
              <div className="mobile-preferences-field">
                <label className="register-field-label settings-field-label">Select your preferred theme</label>
                <button
                  type="button"
                  onClick={() => setShowThemesModal(true)}
                  className="mobile-preference-select-button select-input"
                >
                  <span>{themeOptions.find(t => t.value == preferredTheme)?.label}</span>
                  <ChevronDown className="w-6 h-6 text-[var(--color-text)]" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons fixed at bottom */}
        <div className="action-buttons">
          <button
            className="secondary-button"
            onClick={() => {
              setMobileScreen('profile');
            }}
          >
            Cancel
          </button>
          <button
            className="primary-button"
            onClick={() => {
              toggleTheme();
              setMobileScreen('profile');
            }}
          >
              Save
          </button>
        </div>

        {/* Preferred Theme Selection Modal */}
        {showThemesModal && (
          <div
            className="mobile-modal-overlay"
            onClick={() => setShowThemesModal(false)}
          >
            <div className="mobile-modal" onClick={e => e.stopPropagation()}>
              <div className="mobile-modal-header">
                <h3 className="mobile-modal-title">Select your preferred theme</h3>
                <X
                  className="w-5 h-5 text-[var(--color-text)]"
                  onClick={() => setShowThemesModal(false)}
                />
              </div>
              <div className="mobile-modal-content">
                {themeOptions.map(option => (
                  <label key={option.label} className="mobile-modal-option">
                    <input
                      type="radio"
                      name="aqiStandard"
                      value={option.value}
                      checked={preferredTheme === option.value}
                      onChange={() => {
                        setPreferredTheme(option.value as 'light' | 'dark');
                        setShowThemesModal(false);
                      }}
                      className="mobile-modal-radio"
                    />
                    <span className="mobile-modal-label">{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user: currentUser } = useAuth();
  const [personalInfoExpanded, setPersonalInfoExpanded] = useState(true);
  const [passwordSecurityExpanded, setPasswordSecurityExpanded] =
    useState(false);
  const [unitPreferencesExpanded, setUnitPreferencesExpanded] = useState(false);
  const [interfaceExpanded, setInterfaceExpanded] = useState(true);
  const theme = document.documentElement.getAttribute("data-theme") as 'light' | 'dark' ?? 'light';
  const [preferredTheme, setPreferredTheme] = useState<'light' | 'dark'>(theme);

  // Mobile screen state management
  const [mobileScreen, setMobileScreen] = useState<'profile' | 'account' | 'password' | 'preferences'| 'interface'>('profile');

  // Password visibility states
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Logout states
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Account deletion states
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false);
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);
  const [accountDeletionExpanded, setAccountDeletionExpanded] = useState(false);
  const [deleteAccountError, setDeleteAccountError] = useState('');

  // Form states
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
 
  // Stored as E.164 (e.g. "+971501234567"). Empty string means no number.
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showChangeEmailModal, setShowChangeEmailModal] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const [profilePictureUrl, setProfilePictureUrl] = useState(userData.profilePictureUrl || '');
  const [isUploading, setIsUploading] = useState(false);
  const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Snapshot of the last-loaded/saved personal-info values. Used by Cancel
  // to revert unsaved edits so collapsing the section doesn't preserve them.
  const originalPersonalInfoRef = useRef({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
  });

  // Loading states
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  // Error states
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});
  const [successMessage, setSuccessMessage] = useState('');

  // Devices context
  const devicesContext = useContext(DevicesContext);
  if (!devicesContext) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  const {
    getDevicesForDashboard,
    getParametersForDashboard,
    getAqiInfoData,
    isDemoAccountAllowed,
  } = devicesContext;

  // User Selected Context
  const userSelectedContext = useContext(UserSelectedContext);
  if (!userSelectedContext) {
    throw new Error('useUserSelected must be used within a UserSelectedProvider');
  }
  const { settingsActiveTab, setSettingsActiveTab, hideBottomBar, showBottomBar, isBottomBarHidden } = userSelectedContext;

  // Preferences states
  const [aqiStandardId, setAqiStandardId] = useState<number>(1);
  const [aqiStandards, setAqiStandards] = useState<AqiStandard[]>([]);
  // Initialize timezone to UTC (default), will be overridden by localStorage or API if available
  const [timezone, setTimezone] = useState('');
  const [aqiStandardExpanded, setAqiStandardExpanded] = useState(false);

  // Demo account visibility preference — default true preserves existing behavior
  const [demoAccountExpanded, setDemoAccountExpanded] = useState(false);
  const [showDemoAccount, setShowDemoAccount] = useState<boolean>(() => isDemoAccountAllowed());

  // Dynamic parameter units mapping
  const getUnitOptions = (parameterName: string) => {
    if (parameterName === 'PM2.5') {
      return ['µg/m³', 'mg/m³'];
    } else if (parameterName === 'PM10') {
      return ['µg/m³', 'mg/m³'];
    } else if (parameterName === 'CO₂') {
      return ['µg/m³', 'mg/m³', 'ppm', 'ppb'];
    } else if (parameterName === 'O₃') {
      return ['µg/m³', 'mg/m³', 'ppm', 'ppb'];
    } else if (parameterName === 'NO₂') {
      return ['µg/m³', 'mg/m³', 'ppm', 'ppb'];
    } else if (parameterName === 'CH₂O') {
      return ['µg/m³', 'mg/m³', 'ppm', 'ppb'];
    } else if (parameterName === 'CO') {
      return ['µg/m³', 'mg/m³', 'ppm', 'ppb'];
    } else if (parameterName === 'Temperature') {
      return ['°C', '°F'];
    } else return ['ppm', 'ppb', 'µg/m³', 'mg/m³', '°C', '°F'];
  };

  // Dynamic selected units state
  const [allParameters, setAllParameters] = useState<ParametersWithPreferredUnit[]>([]);

  // Snapshot of the last-loaded/saved preference values (AQI standard,
  // timezone, parameter units, theme). Used to revert unsaved dropdown
  // selections when the user navigates away from Preferences without saving.
  const originalPreferencesRef = useRef({
    aqiStandardId: 1,
    timezone: '',
    allParameters: [] as ParametersWithPreferredUnit[],
    preferredTheme: theme,
  });

  // Revert preference selections back to the last-loaded/saved snapshot.
  const resetPreferences = () => {
    const snapshot = originalPreferencesRef.current;
    setAqiStandardId(snapshot.aqiStandardId);
    setTimezone(snapshot.timezone);
    setAllParameters(snapshot.allParameters.map(p => ({ ...p })));
    setPreferredTheme(snapshot.preferredTheme as 'light' | 'dark');
  };

  // API Key states
  const [apiKey, setApiKey] = useState('sk-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx');

  const tabs: { id: 'account' | 'preferences' | 'api', label: string }[] = [
    { id: 'account', label: 'Account Settings' },
    { id: 'preferences', label: 'Preferences' },
    // { id: 'api', label: 'API Keys' },
  ];

  // Load user profile on component mount
  useEffect(() => {
    loadUserProfile();
  }, []);

  // Clear password fields when navigating away from password screen or when password section is collapsed
  useEffect(() => {
    // Clear password fields when mobile screen changes away from 'password'
    if (mobileScreen !== 'password') {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    }
  }, [mobileScreen]);

  // Clear password fields when password security section is collapsed
  useEffect(() => {
    if (!passwordSecurityExpanded) {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    }
  }, [passwordSecurityExpanded]);

  // Clear password fields when component unmounts (user navigates away from Settings page)
  useEffect(() => {
    return () => {
      setNewPassword('');
      setConfirmPassword('');
      setPasswordError('');
    };
  }, []);

  // Revert unsaved preference selections (AQI standard, timezone, units, theme)
  // when the user switches away from the Preferences tab without saving.
  const prevSettingsTabRef = useRef(settingsActiveTab);
  useEffect(() => {
    if (prevSettingsTabRef.current === 'preferences' && settingsActiveTab !== 'preferences') {
      resetPreferences();
    }
    prevSettingsTabRef.current = settingsActiveTab;
  }, [settingsActiveTab]);

  // Same revert as above, but for the mobile Preferences/Interface screens.
  const prevMobileScreenRef = useRef(mobileScreen);
  useEffect(() => {
    const prevScreen = prevMobileScreenRef.current;
    if ((prevScreen === 'preferences' || prevScreen === 'interface') && mobileScreen !== prevScreen) {
      resetPreferences();
    }
    prevMobileScreenRef.current = mobileScreen;
  }, [mobileScreen]);

  // Load saved preferences (if any) - runs first on mount
  useEffect(() => {
    const loadSavedPreferences = async () => {
      const saved = localStorage.getItem('userPreferences');
      if (saved) {
        const p = JSON.parse(saved);
        if (p.aqiStandardId) setAqiStandardId(p.aqiStandardId);
        if (p.timezone) setTimezone(p.timezone);
        if (typeof p.showDemoAccount === 'boolean') setShowDemoAccount(p.showDemoAccount);

        // Handle new dynamic units format
        if (p.parametersWithPreferredUnit) {
          setAllParameters(p.parametersWithPreferredUnit);
        }

        // Snapshot the loaded values so unsaved edits can be reverted later
        originalPreferencesRef.current = {
          ...originalPreferencesRef.current,
          aqiStandardId: p.aqiStandardId || originalPreferencesRef.current.aqiStandardId,
          timezone: p.timezone || originalPreferencesRef.current.timezone,
          allParameters: (p.parametersWithPreferredUnit || []).map((param: ParametersWithPreferredUnit) => ({ ...param })),
        };

      } else {
        try {
          const [list, userPrefs] = await Promise.all([
            aqiStandardApi.getAqiStandards(),
            aqiStandardApi.getUserAccountPreferences(),
          ]);
          setAqiStandards(list);
          console.log('userPrefs', userPrefs);
          if (userPrefs) {
            // Set AQI standard
            if (userPrefs.aqiStandardName || userPrefs.aqiStandardId) {
              setAqiStandardId(userPrefs.aqiStandardId);
            }

            // Set timezone only if it hasn't been manually changed
            if (userPrefs.timezone) {
              setTimezone(userPrefs.timezone);
            }
            // Set selected units
            if (userPrefs.parametersWithPreferredUnit) {
              setAllParameters(userPrefs.parametersWithPreferredUnit);
            } 
          } else {
            // Fallback to default values
            const hasUSA = list.some(
              (s: AqiStandard) => (s.standardName || '').toUpperCase() === 'USA'
            );
            if (!aqiStandardId && hasUSA) setAqiStandardId(1);
          }

          // Snapshot the loaded values so unsaved edits can be reverted later
          originalPreferencesRef.current = {
            ...originalPreferencesRef.current,
            aqiStandardId: userPrefs?.aqiStandardId || originalPreferencesRef.current.aqiStandardId,
            timezone: userPrefs?.timezone || originalPreferencesRef.current.timezone,
            allParameters: (userPrefs?.parametersWithPreferredUnit || []).map((param: ParametersWithPreferredUnit) => ({ ...param })),
          };

          // Set preferences in localStorage
          localStorage.setItem('userPreferences', JSON.stringify({
            aqiStandardId: userPrefs?.aqiStandardId || null,
            timezone: userPrefs?.timezone || null,
            parametersWithPreferredUnit: userPrefs?.parametersWithPreferredUnit || [],
          }));
        } catch (e) {
          console.error('Failed to load AQI standards or user preferences:', e);
        }
      }
    };

    loadSavedPreferences();
  }, []);
  // Load user profile from API
  const loadUserProfile = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await profileApiService.getUserProfile();

      if (response.success && response.data) {
        const profile = response.data;
        setUsername(profile.username);
        setFirstName(profile.firstName);
        setLastName(profile.lastName);
        setEmail(profile.email);
        // DB stores as "<countryCode> <number>" (e.g. "+91 8585854574").
        // Convert to E.164 for the phone-input component.
        const phoneE164 = toE164(profile.mobileNumber);
        setPhoneNumber(phoneE164);

        // Snapshot for Cancel/revert
        originalPersonalInfoRef.current = {
          firstName: profile.firstName || '',
          lastName: profile.lastName || '',
          email: profile.email || '',
          phoneNumber: phoneE164,
        };
        // Account preferences (AQI standard, timezone, units) are now loaded separately
        // in the useEffect above to avoid conflicts

        // Username is now handled by the auth context
      } else {
        setError(response.error || 'Failed to load profile');
      }
    } catch (error) {
      setError('Failed to load profile information');
      console.error('Error loading profile:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Update personal information
  const handleSavePersonalInfo = async () => {
    try {
      await accountSchema.validate({ FirstName: firstName, LastName: lastName, Email: email, PhoneNumber: phoneNumber }, { abortEarly: false });

      setIsSaving(true);
      setError('');
      setSuccessMessage('');
      setFieldErrors({});

      const updateData: UpdatePersonalInfoRequest = {
        firstName,
        lastName,
        email,
        mobileNumber: e164ToBackendFormat(phoneNumber),
      };

      const response = await profileApiService.updatePersonalInfo(updateData);

      if (response.success) {
        // Snapshot now-saved values so Cancel reverts to current saved state
        originalPersonalInfoRef.current = {
          firstName,
          lastName,
          email,
          phoneNumber,
        };
        setSuccessMessage('Account Settings updated successfully');
        // Clear success message after 3 seconds
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to update Account Settings');
      }
    } catch (validationError) {
      if (validationError instanceof yup.ValidationError) {
        const errors: {[key: string]: string} = {};
        validationError.inner.forEach((error: any) => {
          if (error.path) {
            errors[error.path] = error.message;
          }
        });
        setFieldErrors(prev => ({ ...prev, ...errors }));
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Revert personal-info fields back to the last loaded/saved snapshot.
  const resetPersonalInfo = () => {
    const snapshot = originalPersonalInfoRef.current;
    setFirstName(snapshot.firstName);
    setLastName(snapshot.lastName);
    setEmail(snapshot.email);
    setPhoneNumber(snapshot.phoneNumber);
    setFieldErrors({});
    setError('');
  };

  // Handle file upload
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Unsupported file format. Please upload an image in .jpg, .jpeg, .png, or .gif format.');
      return;
    }

    // Validate file size (5MB limit)
    if (file.size > 5 * 1024 * 1024) {
      setError('File size must be less than 5MB');
      return;
    }

    try {
      setIsUploading(true);
      setError('');
      setSuccessMessage('');

      const response = await profileApiService.uploadProfilePicture(file);

      if (response.success && response.data) {
        const imageUrl = response.data.profilePictureUrl;

        if (!imageUrl) {
          setError(
            'Upload successful but no image URL returned. Please try again.'
          );
          return;
        }

        setProfilePictureUrl(imageUrl);
        localStorage.setItem('userData', JSON.stringify({ ...userData, profilePictureUrl: imageUrl }));
        setSuccessMessage('Profile picture updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        console.error('SettingsPage: Upload failed:', response.error);
        setError(response.error || 'Failed to upload profile picture');
      }
    } catch (error) {
      console.error('SettingsPage: Upload exception:', error);
      setError(
        `Failed to upload profile picture: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Handle upload button click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle profile picture delete
  const handleDeletePhoto = async () => {
    if (!profilePictureUrl || isDeletingPhoto) return;

    try {
      setIsDeletingPhoto(true);
      setError('');
      setSuccessMessage('');

      const response = await profileApiService.deleteProfilePicture();

      if (response.success) {
        setProfilePictureUrl('');
        localStorage.setItem(
          'userData',
          JSON.stringify({ ...userData, profilePictureUrl: '' })
        );
        setSuccessMessage('Profile picture removed successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError(response.error || 'Failed to remove profile picture');
      }
    } catch (err) {
      setError(
        `Failed to remove profile picture: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setIsDeletingPhoto(false);
    }
  };

  const handlePasswordChange = (value: string) => {
    setNewPassword(value);
    const minLength = value.length >= 8;
    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumber = /\d/.test(value);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(value);

    if (
      !minLength ||
      !hasUpperCase ||
      !hasLowerCase ||
      !hasNumber ||
      !hasSpecialChar
    ) {
      setPasswordError('Password does not match requirements');
    } else {
      setPasswordError('');
    }
  };

  const handleConfirmPasswordChange = (value: string) => {
    setConfirmPassword(value);
    // setPasswordError(value !== newPassword ? 'Password does not match' : '');
  };

  // Update password with security features
  const handleUpdatePassword = async () => {
    // Validate password requirements
    const minLength = newPassword.length >= 8;
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const hasLowerCase = /[a-z]/.test(newPassword);
    const hasNumber = /\d/.test(newPassword);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);

    if (
      !minLength ||
      !hasUpperCase ||
      !hasLowerCase ||
      !hasNumber ||
      !hasSpecialChar
    ) {
      setPasswordError('Password does not meet requirements');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Passwords do not match');
      return;
    }

    // Route to set-password for accounts that don't have a password yet
    // (fresh social-login users). No current-password field is required —
    // there's nothing to verify against. For accounts that already have a
    // password (User Credentials, or social users who added one), route to
    // change-password which requires the current password as step-up.
    const isAddingFirstPassword = !currentUser?.hasPassword;
    if (!isAddingFirstPassword && !currentPassword.trim()) {
      setPasswordError('Please enter your current password');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      setError('');
      setSuccessMessage('');
      setPasswordError('');

      let response: { success: boolean; error?: string; message?: string };
      if (isAddingFirstPassword) {
        response = await profileApiService.setPassword({
          newPassword,
          confirmPassword,
        });
      } else {
        const updateData: UpdatePasswordRequest = {
          currentPassword,
          newPassword,
          confirmPassword,
        };
        response = await profileApiService.updatePassword(updateData);
      }

      if (response.success) {
        // Clear password fields
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        if (isAddingFirstPassword) {
          // Adding a first password is a capability addition, not a credential
          // rotation — no session invalidation needed. Keep the user signed in,
          // just surface a confirmation and let them continue.
          setSuccessMessage(
            'Password set successfully. You can now sign in with email and password, and this password will be required for sensitive actions like changing your email.'
          );
          setTimeout(() => setSuccessMessage(''), 6000);
        } else {
          // Password ROTATION bumps TokenVersion server-side → existing sessions
          // will 401 on next authenticated request anyway. Explicit logout makes
          // this visible to the user immediately with an accurate message.
          setSuccessMessage(
            'Password updated successfully! For security reasons, you will be logged out and must sign in again with your new password.'
          );

          setTimeout(async () => {
            try {
              await logout();
              window.location.replace(`${process.env.PUBLIC_URL}/login`);
            } catch (error) {
              console.error('Logout error:', error);
              localStorage.removeItem('userData');
              localStorage.removeItem('accessToken');
              localStorage.removeItem('refreshToken');
              sessionStorage.clear();
              window.location.replace(`${process.env.PUBLIC_URL}/login`);
            }
          }, 3000);
        }
      } else {
        // Handle specific error cases
        if (response.error?.includes('same password')) {
          setPasswordError(
            'New password must be different from your current password.'
          );
        } else {
          setPasswordError(
            response.error || 'Failed to update password. Please try again.'
          );
        }
      }
    } catch (error) {
      console.error('Error updating password:', error);
      setPasswordError(
        'Network error. Please check your connection and try again.'
      );
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleCopyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    // You could add a toast notification here
  };

  const generateNewApiKey = () => {
    const newKey =
      'sk-' +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    setApiKey(newKey);
  };

  // Save All Preferences (unified handler for both mobile and desktop)
  const handleSaveAllPreferences = async () => {
    try {
      setIsSaving(true);
      setError('');
      setSuccessMessage('');

      // Save AQI Standard and Timezone
      try {
        const success = await aqiStandardApi.updateUserAqiStandardandTimezone(aqiStandardId, timezone);
        if (success) {
          const saved = localStorage.getItem('userPreferences');
          if (saved) {
            const p = JSON.parse(saved);
            localStorage.setItem('userPreferences', JSON.stringify({
              ...p,
              timezone: timezone,
              aqiStandardId: aqiStandardId,
            }));
          }
          originalPreferencesRef.current.aqiStandardId = aqiStandardId;
          originalPreferencesRef.current.timezone = timezone;
         // setSuccessMessage('AQI standard and timezone preferences saved successfully!');
          setTimeout(() => setSuccessMessage(''), 2000);
        } else {
          // update states with local storage data if failed to save
          const saved = localStorage.getItem('userPreferences');
          if (saved) {
            const p = JSON.parse(saved);
            setAqiStandardId(p.aqiStandardId);
            setTimezone(p.timezone);
          }
          setError('Failed to save AQI standard and timezone preferences to server');
        }
      } catch (e) {
        console.warn('Could not update AQI standard and timezone preferences on server:', e);
      }

      // Save Parameter Units Preferences
      const parameterUnitsPreferences: updateParameterUnitsPreferencesRequest = {
        parameters: allParameters,
      };

      try {
        const success = await aqiStandardApi.updateUserParameterUnitsPreferences(parameterUnitsPreferences);
        if (success) {
          const saved = localStorage.getItem('userPreferences');
          if (saved) {
            const p = JSON.parse(saved);
            localStorage.setItem('userPreferences', JSON.stringify({
              ...p,
              parametersWithPreferredUnit: allParameters,
            }));
          }
          originalPreferencesRef.current.allParameters = allParameters.map(p => ({ ...p }));
          setSuccessMessage('Preferences saved successfully!');
          setTimeout(() => setSuccessMessage(''), 2000);
        } else {
            // update states with local storage data if failed to save
            const saved = localStorage.getItem('userPreferences');
            if (saved) {
              const p = JSON.parse(saved);
              setAllParameters(p.parametersWithPreferredUnit);
            }
          setError('Failed to save preferences to server');
        }
      } catch (e) {
        console.warn('Could not update parameter units preferences on server:', e);
        setError('Failed to save preferences to server');
      }

      // Persist demo account visibility alongside other prefs so mobile (which
      // uses one combined Save button) picks it up. Desktop's dedicated Demo
      // Account card still uses handleSaveDemoAccountPreference.
      try {
        const saved = localStorage.getItem('userPreferences');
        const p = saved ? JSON.parse(saved) : {};
        localStorage.setItem('userPreferences', JSON.stringify({
          ...p,
          showDemoAccount,
        }));
      } catch (e) {
        console.warn('Could not persist demo account preference:', e);
      }

      await getDevicesForDashboard();
      await getParametersForDashboard();
      await getAqiInfoData();
    } catch (error) {
      setError('Failed to save preferences');
    } finally {
      setIsSaving(false);
    }
  };

  // Save demo account visibility preference
  // const handleSaveDemoAccountPreference = async () => {
  //   try {
  //     setIsSaving(true);
  //     setError('');
  //     setSuccessMessage('');

  //     const saved = localStorage.getItem('userPreferences');
  //     const p = saved ? JSON.parse(saved) : {};
  //     localStorage.setItem('userPreferences', JSON.stringify({
  //       ...p,
  //       showDemoAccount,
  //     }));

  //     // Reload dashboard devices so demo visibility takes effect immediately.
  //     // DevicesContext reads the preference and suppresses/includes the demo
  //     // device accordingly, which in turn enables/disables alert and share-link
  //     // creation (both gate on `devices.length === 0 && !demoDevice`).
  //     await getDevicesForDashboard();

  //     setSuccessMessage('Preferences saved successfully!');
  //     setTimeout(() => setSuccessMessage(''), 2000);
  //   } catch (e) {
  //     setError('Failed to save demo account preference');
  //   } finally {
  //     setIsSaving(false);
  //   }
  // };


  // Form validation functions
  const isPasswordFormValid = (): boolean => {
    const hasCurrent = !currentUser?.hasPassword || currentPassword.trim().length > 0;
    const minLength = newPassword.length >= 8;
    const hasNumber = /\d/.test(newPassword);
    const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    const hasUpperCase = /[A-Z]/.test(newPassword);
    const passwordsMatch = newPassword === confirmPassword;

    return hasCurrent && minLength && hasNumber && hasSymbol && hasUpperCase && passwordsMatch;
  };

  // Handle logout confirmation
  const handleLogoutConfirm = async () => {
    setIsLoggingOut(true);
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      // Still navigate to login even if logout fails
      navigate('/login');
    } finally {
      setIsLoggingOut(false);
      setShowLogoutModal(false);
    }
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  // Handle account deletion
  const handleDeleteAccount = async (currentPassword: string) => {
    setIsDeletingAccount(true);
    setDeleteAccountError('');
    try {
      const userId = userData?.userId;
      if (!userId) {
        setError('User ID not found. Please try logging out and logging back in.');
        setIsDeletingAccount(false);
        setShowDeleteAccountModal(false);
        return;
      }

      // For accounts that have a password, require it as a step-up security
      // check before deletion. Social-login users without a local password
      // skip this check.
      if (currentUser?.hasPassword && !currentPassword.trim()) {
        setDeleteAccountError('Please enter your current password to continue.');
        setIsDeletingAccount(false);
        return;
      }

      const response = await apiService.deleteAccount(
        userId,
        currentUser?.hasPassword ? currentPassword : undefined,
      );

      if (response.success) {
        // Clear all local data
        localStorage.clear();

        // Navigate to login page
        navigate('/login');
      } else {
        // Keep the modal open so the user can correct the password and retry.
        const message = response.error || 'Failed to delete account. Please try again.';
        const isAuthFailure = /password|incorrect|invalid|unauthorized/i.test(message);
        if (isAuthFailure) {
          setDeleteAccountError(message);
        } else {
          setError(message);
          setShowDeleteAccountModal(false);
        }
        setIsDeletingAccount(false);
      }
    } catch (error) {
      setError(`Account deletion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setIsDeletingAccount(false);
      setShowDeleteAccountModal(false);
    }
  };

  const handleDeleteAccountCancel = () => {
    setShowDeleteAccountModal(false);
    setDeleteAccountError('');
  };

  // handle theme change
  const toggleTheme = () => {
    localStorage.setItem("data-theme", preferredTheme);
    document.documentElement.setAttribute("data-theme", preferredTheme);
    originalPreferencesRef.current.preferredTheme = preferredTheme;
    setSuccessMessage('Theme updated successfully!');
    setTimeout(() => setSuccessMessage(''), 2000);
  };

  // Handle parameter unit change
  const handleParameterUnitChange = (e: React.ChangeEvent<HTMLSelectElement>, parameterId: number) => {
    const unitName = e.target.value;
    const parameter = allParameters.find(parameter => parameter.parameterId === parameterId);
    if (parameter) {
      parameter.preferredUnitName = unitName;
      setAllParameters(allParameters.map(p => p.parameterId === parameterId ? parameter : p));
    }
  };

  useEffect(() => {
    if (isBottomBarHidden && mobileScreen === 'profile' ) {
      showBottomBar();
    } 
  }, [mobileScreen]);

  // E.164 value comes straight from the phone-input component — store as-is.
  const handlePhoneNumberChange = async (value: string) => {
    setPhoneNumber(value);
    try {
      await accountSchema.validateAt('PhoneNumber', { PhoneNumber: value });
      setFieldErrors(prev => {
        if (!prev.PhoneNumber) return prev;
        const next = { ...prev };
        delete next.PhoneNumber;
        return next;
      });
    } catch (validationError) {
      setFieldErrors(prev => ({ ...prev, PhoneNumber: (validationError as Error).message }));
    }
  };

  const openChangeEmailModal = () => setShowChangeEmailModal(true);

  const handleChangeEmailSubmit = async (
    nextEmail: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      // Accounts without a local password yet (fresh social-login users) need
      // to set one before the email-change step-up will accept them. For these
      // users, the "New Password" field in the Change Email modal is doing
      // double duty: we first call set-password to store it, then proceed with
      // the email change using the same password as the current-password
      // check. Accounts with a password already — including social users who
      // previously set one — skip this branch and go straight to
      // updatePersonalInfo which verifies the password as current.
      if (!currentUser?.hasPassword) {
        const setResp = await profileApiService.setPassword({
          newPassword: password,
          confirmPassword: password,
        });
        if (!setResp.success) {
          const alreadySet = /already have a password/i.test(setResp.error || '');
          if (!alreadySet) {
            return { success: false, error: setResp.error || 'Failed to set password.' };
          }
          // already-set ⇒ client state was stale (server disagrees). Fall through
          // and let updatePersonalInfo verify the password they just typed.
        }
      }

      // Two-step email change: the backend sends a confirmation link to `nextEmail`
      // and does NOT update our sign-in email until the user clicks that link.
      // We stay signed in with the current email until confirmation revokes the session.
      const updateResp = await profileApiService.updatePersonalInfo({
        firstName,
        lastName,
        email: nextEmail,
        mobileNumber: e164ToBackendFormat(phoneNumber),
        password,
        isEmailVerified: false,
      });

      if (!updateResp.success) {
        return { success: false, error: updateResp.error || 'Failed to update email. Please try again.' };
      }

      setShowChangeEmailModal(false);
      setSuccessMessage(
        `We’ve sent a confirmation link to ${nextEmail}. Click it to finish updating your sign-in email. Until then, you’ll keep signing in with ${email}.`
      );
      setTimeout(() => setSuccessMessage(''), 6000);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to update email. Please try again.',
      };
    }
  };

  /* Account Fields */
  const accountFields = [
    {
      label: 'First Name',
      value: firstName,
      onChange: setFirstName,
      icon: `${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold.svg`,
      type: 'text',
      required: true,
    },
    {
      label: 'Last Name',
      value: lastName,
      onChange: setLastName,
      icon: `${process.env.PUBLIC_URL}/SVGIcons/solar_user-bold.svg`,
      type: 'text',
      required: true,
    },
    {
      label: 'Email',
      value: email,
      onChange: setEmail,
      icon: `${process.env.PUBLIC_URL}/SVGIcons/Email.svg`,
      type: 'email',
      required: true,
    },
    {
      label: 'Phone Number',
      value: phoneNumber,
      onChange: handlePhoneNumberChange,
      icon: `${process.env.PUBLIC_URL}/SVGIcons/Mobile.svg`,
      type: 'tel',
      required: false,
    },
  ];

  /* Password Fields — Current Password only shown for accounts that already
     have a password. Fresh social-login users (hasPassword === false) go
     through set-password and have nothing to verify against. */
  const passwordFields = [
    ...(currentUser?.hasPassword
      ? [
          {
            label: 'Current Password',
            value: currentPassword,
            onChange: setCurrentPassword,
            showPassword: showCurrentPassword,
            setShowPassword: setShowCurrentPassword,
            placeholder: 'Enter current password',
          },
        ]
      : []),
    {
      label: 'New Password',
      value: newPassword,
      onChange: setNewPassword,
      showPassword: showNewPassword,
      setShowPassword: setShowNewPassword,
      placeholder: 'Enter new password',
    },
    {
      label: 'Confirm Password',
      value: confirmPassword,
      onChange: setConfirmPassword,
      showPassword: showConfirmPassword,
      setShowPassword: setShowConfirmPassword,
      placeholder: 'Enter confirm password',
    },
  ];

  return (
    <>
      <DashboardLayout>
        {/* Desktop Layout - Hidden on mobile */}
        <div className="settings-container">
          {/* Tabs */}
          {!isLoading && (
            <div className="tabs-container desktop-only zoomOutLevel">
              <nav className="tabs-nav">
                {tabs.map(tab => {
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setSettingsActiveTab(tab.id)}
                      className={`tab-button ${
                        settingsActiveTab === tab.id ? 'active' : ''
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </nav>
            </div>
          )}

          {/* Error and Success Messages */}
          {error && (
            <div className="error-message">
              <div className="error-content">
                <div className="error-icon">
                  <span className="error-icon-text">!</span>
                </div>
                <span className="error-text">{error}</span>
                <X
                  className="h-5 w-5 ml-auto text-[#E67E7D] cursor-pointer hover:text-[#C42B2A]"
                  onClick={() => setError('')}
                />
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div
              className={`success-message ${
                successMessage.includes('logged out') ? 'blue' : 'green'
              }`}
            >
              <div className="success-content">
                <div
                  className={`success-icon ${
                    successMessage.includes('logged out') ? 'blue' : 'green'
                  }`}
                >
                  {successMessage.includes('logged out') ? (
                    <span className="success-icon-text">!</span>
                  ) : (
                    <Check className="h-3 w-3 text-white" />
                  )}
                </div>
                <div>
                  <span
                    className={`success-text ${
                      successMessage.includes('logged out') ? 'blue' : 'green'
                    }`}
                  >
                    {successMessage}
                  </span>
                  {successMessage.includes('logged out') && (
                    <p className="success-subtext">
                      Redirecting to login page in 3 seconds...
                    </p>
                  )}
                </div>
                <X
                  className="h-5 w-5 ml-auto text-[#22C55E] cursor-pointer hover:text-[#15803D]"
                  onClick={() => setSuccessMessage('')}
                />
              </div>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="h-[calc(100dvh-5rem)] loading-container">
              <div className="h-7 w-7 border-b-2 border-[var(--gradient-button-left-color)] mb-3 animate-spin rounded-full"></div>
              <span className="text-sm text-[var(--color-sub-title-text)]">
                Loading profile information...
              </span>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileUpload}
            className="hidden-input"
          />

          {/* Tab Content */}
          {!isLoading && (
            <div className="tab-content-container desktop-only zoomOutLevel">
              {settingsActiveTab === 'account' && (
                <div className="tab-content">
                  {/* Header */}
                  <div className="settings-header">
                    <h1 className="settings-title">
                      Account Settings{' '}
                      <p className="username-badge">{username || 'N/A'}</p>
                    </h1>
                    <p className="settings-subtitle">
                      Manage your account details and security preferences.
                    </p>
                  </div>
                  {/* Personal Information Section */}
                  <div className="card">
                    <div
                      className="card-header"
                      onClick={() => {
                        setPersonalInfoExpanded(!personalInfoExpanded);
                        passwordSecurityExpanded && setPasswordSecurityExpanded(false);
                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/UserIcon.svg`}
                            alt="User"
                          />
                        </div>
                        <div>
                          <h3 className="card-title">Personal Information</h3>
                          <p className="card-subtitle">
                            Update your personal details and contact information
                          </p>
                        </div>
                      </div>
                      {personalInfoExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {personalInfoExpanded && (
                      <div className="card-body">
                        <div className="card-body-content">
                          {/* Left Column - Form Fields */}
                          <div className="grid lg:grid-cols-2 grid-cols-1 gap-x-6 gap-y-4 col-span-3">
                            {accountFields.map(field => {
                              const fieldName = field.label.replace(' ', '');
                              const isPhone = fieldName === 'PhoneNumber';
                              const isEmail = fieldName === 'Email';
                              return (
                                <div key={field.label} className="form-field">
                                  <label className="register-field-label settings-field-label" htmlFor={`account-${fieldName}`}>
                                    {field.label}
                                    {field.required && <span className="text-red-500"> *</span>}
                                  </label>
                                  {isPhone ? (
                                    <PhoneNumberInput
                                      inputId={`account-${fieldName}`}
                                      inputName="mobileNumber"
                                      value={field.value}
                                      onChange={field.onChange}
                                      defaultCountry="ae"
                                      placeholder="Enter mobile number"
                                      error={fieldErrors[fieldName]}
                                    />
                                  ) : (
                                    <>
                                      <div className="register-input-with-icon" style={isEmail ? { position: 'relative' } : undefined}>
                                        <img
                                          src={field.icon}
                                          alt={field.label}
                                          className="settings-input-icon"
                                        />
                                        <input
                                          id={`account-${fieldName}`}
                                          type={field.type}
                                          value={field.value}
                                          readOnly={isEmail}
                                          onChange={async e => {
                                            if (isEmail) return;
                                            try {
                                              field.onChange(e.target.value);
                                              await accountSchema.validateAt(
                                                fieldName,
                                                { [fieldName]: e.target.value }
                                              );
                                              setFieldErrors(prev => ({
                                                ...prev,
                                                [fieldName]: '',
                                              }));
                                            } catch (validationError) {
                                              setFieldErrors(prev => ({
                                                ...prev,
                                                [fieldName]: (
                                                  validationError as Error
                                                ).message,
                                              }));
                                            }
                                          }}
                                          className={`border-0 register-input settings-input focus:border ${isEmail ? 'cursor-not-allowed pr-10' : ''}`}
                                        />
                                        {isEmail && (
                                          <button
                                            type="button"
                                            onClick={openChangeEmailModal}
                                            aria-label="Change email"
                                            className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-[var(--button-hover-background)] text-[var(--color-text)]"
                                          >
                                            <Pencil className="w-4 h-4" />
                                          </button>
                                        )}
                                      </div>
                                      {fieldErrors[fieldName] && (
                                        <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                          <AlertCircle className="h-4 w-4" />
                                          {fieldErrors[fieldName]}
                                        </p>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Right Column - Profile Photo */}
                          <div className="profile-photo-section justify-self-end">
                            <div className="profile-photo-container">
                              
                              {profilePictureUrl ? (
                                <img
                                  src={profilePictureUrl}
                                  alt="Profile"
                                  className="profile-photo md:h-[12rem]"
                                />
                              ) : (
                                <div className='profile-photo md:h-[12rem]'>
                                  <svg xmlns="http://www.w3.org/2000/svg" className='profile-photo-icon' viewBox="0 0 640 640" fill = {theme == "light" ? '#363636' : '#fff'}>
                                    <path d="M320 312C386.3 312 440 258.3 440 192C440 125.7 386.3 72 320 72C253.7 72 200 125.7 200 192C200 258.3 253.7 312 320 312zM290.3 368C191.8 368 112 447.8 112 546.3C112 562.7 125.3 576 141.7 576L498.3 576C514.7 576 528 562.7 528 546.3C528 447.8 448.2 368 349.7 368L290.3 368z" />
                                  </svg>
                                </div>
                              )}


                              <div className="profile-photo-actions">
                                <button
                                  type="button"
                                  onClick={handleUploadClick}
                                  disabled={isUploading || isDeletingPhoto}
                                  aria-label={profilePictureUrl ? 'Change profile picture' : 'Upload profile picture'}
                                  title={profilePictureUrl ? 'Change photo' : 'Upload photo'}
                                  className="profile-photo-icon-btn"
                                >
                                  {isUploading ? (
                                    <div className="h-4 w-4 border-b-2 border-[var(--color-terms-link)] animate-spin rounded-full"></div>
                                  ) : (
                                    <Pencil className="w-4 h-4" />
                                  )}
                                </button>
                                {profilePictureUrl && (
                                  <button
                                    type="button"
                                    onClick={handleDeletePhoto}
                                    disabled={isUploading || isDeletingPhoto}
                                    aria-label="Remove profile picture"
                                    title="Remove photo"
                                    className="profile-photo-icon-btn profile-photo-icon-btn--danger"
                                  >
                                    {isDeletingPhoto ? (
                                      <div className="h-4 w-4 border-b-2 border-[#dc2626] animate-spin rounded-full"></div>
                                    ) : (
                                      <Trash2 className="w-4 h-4" />
                                    )}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="action-buttons">
                          <button
                            onClick={handleSavePersonalInfo}
                            disabled={isSaving || Object.values(fieldErrors).some(error => error !== '')}
                            className="primary-button"
                          >
                            {isSaving && (
                              <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
                            )}
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                          </button>
                          <button
                            onClick={() => {
                              // Revert any unsaved edits, then collapse the section
                              resetPersonalInfo();
                              setPersonalInfoExpanded(false);
                            }}
                            disabled={isSaving}
                            className="secondary-button"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Password & Security Section */}
                  <div className="card">
                    <div
                      className="card-header"
                      onClick={() =>{
                        const wasExpanded = passwordSecurityExpanded;
                        setPasswordSecurityExpanded(!passwordSecurityExpanded);
                        personalInfoExpanded && setPersonalInfoExpanded(false);
                        // Clear password fields when collapsing the section
                        if (wasExpanded) {
                          setNewPassword('');
                          setConfirmPassword('');
                          setPasswordError('');
                        }
                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/LockIcon.svg`}
                            alt="User"
                          />
                        </div>
                        <div>
                          <h3 className="card-title">Password & Security</h3>
                          <p className="card-subtitle">
                            Ensure your account stays secure by using a strong
                            password
                          </p>
                        </div>
                      </div>
                      {passwordSecurityExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {passwordSecurityExpanded && (
                      <div className="card-body max-w-3xl">
                        {/* Security Warning — copy depends on whether the user
                            is SETTING a first password (no session rotation)
                            or CHANGING an existing one (all sessions invalidated). */}
                        <div className="security-warning">
                          <div className="security-warning-content">
                            <div className="security-warning-icon">
                              <span className="security-warning-icon-text">
                                !
                              </span>
                            </div>
                            <div>
                              <h4 className="security-warning-title">
                                {currentUser?.hasPassword ? 'Security Notice' : 'Add a password'}
                              </h4>
                              <p className="security-warning-text">
                                {currentUser?.hasPassword
                                  ? "Changing your password will automatically log you out of all devices for security reasons. You'll need to sign in again with your new password."
                                  : "You signed up with a social account, so you don't have a password yet. Set one here to enable email + password sign-in and to authorise sensitive actions such as changing your email."}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="settings-password-container">
                          {[
                            // Current Password is shown only for accounts that
                            // already have one. A social user adding their
                            // first password (hasPassword === false) routes
                            // through set-password, which has nothing to verify
                            // against.
                            ...(currentUser?.hasPassword
                              ? [
                                  {
                                    label: 'Current Password',
                                    value: currentPassword,
                                    onChange: setCurrentPassword,
                                    showPassword: showCurrentPassword,
                                    setShowPassword: setShowCurrentPassword,
                                    placeholder: 'Enter current password',
                                  },
                                ]
                              : []),
                            {
                              label: 'New Password',
                              value: newPassword,
                              onChange: handlePasswordChange,
                              showPassword: showNewPassword,
                              setShowPassword: setShowNewPassword,
                              placeholder: 'Enter new password',
                            },
                            {
                              label: 'Confirm Password',
                              value: confirmPassword,
                              onChange: handleConfirmPasswordChange,
                              showPassword: showConfirmPassword,
                              setShowPassword: setShowConfirmPassword,
                              placeholder: 'Enter confirm password',
                            },
                          ].map((field, index) => (
                            <div key={field.label} className="settings-input-password">
                              <label className="register-field-label settings-field-label">
                                {field.label}
                                <span className="text-red-500"> *</span>
                              </label>
                              <div className="password-field-container">
                                <img
                                  src={`${process.env.PUBLIC_URL}/SVGIcons/tabler_lock-filled.svg`}
                                  alt="Lock"
                                  className="settings-input-icon"
                                />
                                <input
                                  type={field.showPassword ? 'text' : 'password'}
                                  value={field.value}
                                  onChange={e => field.onChange(e.target.value)}
                                  className="border-0 register-input settings-input focus:border"
                                  placeholder={field.placeholder}
                                />
                                <button
                                  type="button"
                                  onClick={() =>
                                    field.setShowPassword(!field.showPassword)
                                  }
                                  className="password-toggle"
                                >
                                  {field.showPassword ? (
                                    <svg
                                      width="22"
                                      height="15"
                                      viewBox="0 0 22 15"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M11 4.5C10.2044 4.5 9.44129 4.81607 8.87868 5.37868C8.31607 5.94129 8 6.70435 8 7.5C8 8.29565 8.31607 9.05871 8.87868 9.62132C9.44129 10.1839 10.2044 10.5 11 10.5C11.7956 10.5 12.5587 10.1839 13.1213 9.62132C13.6839 9.05871 14 8.29565 14 7.5C14 6.70435 13.6839 5.94129 13.1213 5.37868C12.5587 4.81607 11.7956 4.5 11 4.5ZM11 12.5C9.67392 12.5 8.40215 11.9732 7.46447 11.0355C6.52678 10.0979 6 8.82608 6 7.5C6 6.17392 6.52678 4.90215 7.46447 3.96447C8.40215 3.02678 9.67392 2.5 11 2.5C12.3261 2.5 13.5979 3.02678 14.5355 3.96447C15.4732 4.90215 16 6.17392 16 7.5C16 8.82608 15.4732 10.0979 14.5355 11.0355C13.5979 11.9732 12.3261 12.5 11 12.5ZM11 0C6 0 1.73 3.11 0 7.5C1.73 11.89 6 15 11 15C16 15 20.27 11.89 22 7.5C20.27 3.11 16 0 11 0Z"
                                        fill="#B5BBC0"
                                      />
                                    </svg>
                                  ) : (
                                    <svg
                                      width="22"
                                      height="19"
                                      viewBox="0 0 22 19"
                                      fill="none"
                                      xmlns="http://www.w3.org/2000/svg"
                                    >
                                      <path
                                        d="M10.83 6L14 9.16V9C14 8.20435 13.6839 7.44129 13.1213 6.87868C12.5587 6.31607 11.7956 6 11 6H10.83ZM6.53 6.8L8.08 8.35C8.03 8.56 8 8.77 8 9C8 9.79565 8.31607 10.5587 8.87868 11.1213C9.44129 11.6839 10.2044 12 11 12C11.22 12 11.44 11.97 11.65 11.92L13.2 13.47C12.53 13.8 11.79 14 11 14C9.67392 14 8.40215 13.4732 7.46447 12.5355C6.52678 11.5979 6 10.3261 6 9C6 8.21 6.2 7.47 6.53 6.8ZM1 1.27L3.28 3.55L3.73 4C2.08 5.3 0.78 7 0 9C1.73 13.39 6 16.5 11 16.5C12.55 16.5 14.03 16.2 15.38 15.66L15.81 16.08L18.73 19L20 17.73L2.27 0M11 4C12.3261 4 13.5979 4.52678 14.5355 5.46447C15.4732 6.40215 16 7.67392 16 9C16 9.64 15.87 10.26 15.64 10.82L18.57 13.75C20.07 12.5 21.27 10.86 22 9C20.27 4.61 16 1.5 11 1.5C9.6 1.5 8.26 1.75 7 2.2L9.17 4.35C9.74 4.13 10.35 4 11 4Z"
                                        fill="#B5BBC0"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                              {index === 0 &&
                                newPassword &&
                                (() => {
                                  const minLength = newPassword.length >= 8;
                                  const hasNumber = /\d/.test(newPassword);
                                  const hasSymbol = /[!@#$%^&*(),.?":{}|<>]/.test(
                                    newPassword
                                  );
                                  const hasUpperCase = /[A-Z]/.test(newPassword);
                                  const allValid =
                                    minLength &&
                                    hasNumber &&
                                    hasSymbol &&
                                    hasUpperCase;

                                  // Only show validation if not all requirements are met
                                  if (!allValid) {
                                    return (
                                      <div className="password-validation">
                                        {!minLength && (
                                          <p className="password-validation-item">
                                            ✗ Must be 8 characters
                                          </p>
                                        )}
                                        {!hasNumber && (
                                          <p className="password-validation-item">
                                            ✗ Must contain a number
                                          </p>
                                        )}
                                        {!hasSymbol && (
                                          <p className="password-validation-item">
                                            ✗ Must contain a symbol
                                          </p>
                                        )}
                                        {!hasUpperCase && (
                                          <p className="password-validation-item">
                                            ✗ Must contain an uppercase letter
                                          </p>
                                        )}
                                      </div>
                                    );
                                  }
                                  return null;
                                })()}
                              {field.label === 'Confirm Password' &&
                                newPassword &&
                                newPassword !== confirmPassword && (
                                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                                    <AlertCircle className="h-4 w-4" />
                                    {confirmPassword
                                      ? 'Passwords do not match'
                                      : 'Please confirm your password'}
                                  </p>
                                )}
                            </div>
                          ))}
                        </div>

                        {/* Password Error Message */}
                        {passwordError && (
                          <div className="password-error">
                            <div className="password-error-content">
                              <div className="password-error-icon">
                                <span className="password-error-icon-text">
                                  !
                                </span>
                              </div>
                              <span className="password-error-text">
                                {passwordError}
                              </span>
                              <X
                                className="h-5 w-5 ml-auto text-[#E67E7D] cursor-pointer hover:text-[#C42B2A]"
                                onClick={() => setPasswordError('')}
                              />
                            </div>
                          </div>
                        )}

                        <div className="action-buttons">
                          <button
                            onClick={handleUpdatePassword}
                            disabled={isUpdatingPassword}
                            className="primary-button"
                          >
                            {isUpdatingPassword && (
                              <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
                            )}
                            <span>
                              {isUpdatingPassword
                                ? (currentUser?.hasPassword ? 'Updating...' : 'Setting...')
                                : (currentUser?.hasPassword ? 'Update' : 'Set Password')}
                            </span>
                          </button>
                          <button
                            onClick={() => {
                              // Reset password fields
                              setCurrentPassword('');
                              setNewPassword('');
                              setConfirmPassword('');
                              setPasswordError('');
                            }}
                            disabled={isUpdatingPassword}
                            className="secondary-button"
                          >
                            Clear
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Account Deletion Section */}
                  <div className="card border-red-200">
                    <div
                      className="card-header"
                      onClick={() => {
                        setAccountDeletionExpanded(!accountDeletionExpanded);
                        passwordSecurityExpanded && setPasswordSecurityExpanded(false);
                        personalInfoExpanded && setPersonalInfoExpanded(false);
                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/LockIcon.svg`}
                            alt="Delete Account"
                            className="opacity-75"
                          />
                        </div>
                        <div>
                          <h3 className="card-title text-red-600">Delete Account</h3>
                          <p className="card-subtitle">
                            Permanently delete your account and all associated data
                          </p>
                        </div>
                      </div>
                      {accountDeletionExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {accountDeletionExpanded && (
                      <div className="card-body max-w-3xl">
                        {/* Warning Message */}
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                          <div className="flex items-start gap-3">
                            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <h4 className="text-red-800 font-semibold mb-2">
                                ⚠️ This action cannot be undone
                              </h4>
                              <p className="text-red-700 text-sm mb-2">
                                Deleting your account will permanently remove:
                              </p>
                              <ul className="list-disc list-inside text-red-700 text-sm space-y-1 ml-2">
                                <li>Your profile and personal information</li>
                                <li>All your devices and their historical data</li>
                                <li>Your preferences and settings</li>
                                <li>All reports and analytics data</li>
                              </ul>
                              <p className="text-red-700 text-sm mt-3 font-medium">
                                Your devices will be disassociated from your account but will remain in the system.
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                          <p className="text-yellow-800 text-sm">
                            <strong>Note:</strong> If you need to temporarily disable your account instead, 
                            please contact support at{' '}
                            <a href="mailto:smartecosupport@ispatialtec.com" className="underline">
                              smartecosupport@ispatialtec.com
                            </a>
                          </p>
                        </div>

                        <div className="action-buttons">
                          <button
                            onClick={() => setShowDeleteAccountModal(true)}
                            className="danger-button"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => setAccountDeletionExpanded(false)}
                            className="secondary-button"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {settingsActiveTab === 'preferences' && (
                <div className="tab-content">
                  {/* Header */}
                  <div className="settings-header">
                    <h2 className="settings-title">Preferences</h2>
                    <p className="settings-subtitle">
                      Configure your personal settings and application behavior.
                    </p>
                  </div>

                  {/* Interface Section */}
                  <div className="card">
                    <div
                      className="card-header"
                      onClick={() => {
                        setInterfaceExpanded(!interfaceExpanded);
                        unitPreferencesExpanded && setUnitPreferencesExpanded(false);
                        aqiStandardExpanded && setAqiStandardExpanded(false);
                        demoAccountExpanded && setDemoAccountExpanded(false);
                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/interfaceIcon.svg`}
                            alt="Interface"
                          />
                        </div>
                        <div>
                          <h3 className="card-title">Interface</h3>
                          <p className="card-subtitle">
                            Adjust the appearance and layout of the application interface.
                          </p>
                        </div>
                      </div>
                      {interfaceExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {interfaceExpanded && (
                      <div className="card-body">
                        <div className="grid grid-cols-2 gap-4 max-w-5xl">
                          <div className="select-form-field">
                            <label className="register-field-label settings-field-label">
                              Select your preferred theme
                            </label>
                            <div className="register-input-with-icon">
                              <select
                                value={preferredTheme}
                                onChange={e => {
                                  const value = e.target.value as 'light' | 'dark';
                                  setPreferredTheme(value);
                                }}
                                className="select-input"
                              >
                                <option value="light">
                                  Light Theme
                                </option>
                                <option value="dark">
                                  Dark Theme
                                </option>
                              </select>
                              <ChevronDown className="w-6 h-6 select-input-icon text-[var(--color-text)]" />
                            </div>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="action-buttons">
                          <button onClick={() => setInterfaceExpanded(false)} className="secondary-button">Cancel</button>
                          <button
                            onClick={toggleTheme}
                            disabled={isSaving}
                            className="primary-button"
                          >
                            {isSaving && (
                              <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
                            )}
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                

                  {/* AQI Standard and Time Zone Section */}
                  <div className="card">
                    <div
                      className="card-header"
                      onClick={() => {
                        setAqiStandardExpanded(!aqiStandardExpanded)
                        unitPreferencesExpanded && setUnitPreferencesExpanded(false);
                        interfaceExpanded && setInterfaceExpanded(false);
                        demoAccountExpanded && setDemoAccountExpanded(false);
                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/LocalIcon.svg`}
                            alt="Unit Preferences"
                          />
                        </div>
                        <div>
                          <h3 className="card-title">AQI Standard & Time Zone</h3>
                          <p className="card-subtitle">
                            Set your preferred AQI standard and time zone for the app
                          </p>
                        </div>
                      </div>
                      {aqiStandardExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {aqiStandardExpanded && (
                      <div className="card-body">
                        <div className="aqi-standard-card-content max-w-5xl">
                          <div className="select-form-field">
                            <label className="register-field-label settings-field-label">
                            Select your AQI Standard
                            </label>
                            <div className="register-input-with-icon">
                              <select
                                value={aqiStandardId}
                                onChange={e => setAqiStandardId(parseInt(e.target.value))}
                                className="select-input"
                              >
                                {(aqiStandards.length > 0
                                  ? aqiStandards
                                  : [
                                      {
                                        id: 1,
                                        standardName: 'USA',
                                        description: 'United States',
                                      },
                                      {
                                        id: 2,
                                        standardName: 'UAE',
                                        description: 'United Arab Emirates',
                                      },
                                      {
                                        id: 3,
                                        standardName: 'INDIA',
                                        description: 'India',
                                      },
                                    ]
                                ).map((s: any) => (
                                  <option key={s.id} value={s.id}>
                                    {`${s.description} (${s.standardName})`}
                                  </option>
                                ))}
                              </select>
                              <ChevronDown className="w-6 h-6 select-input-icon text-[var(--color-text)]" />
                            </div>
                          </div>

                          <div className="select-form-field">
                              <label className="register-field-label settings-field-label">
                                Select your Time Zone
                              </label>
                              <SearchableSelect
                                value={timezone}
                                onChange={setTimezone}
                                options={getAllTimezones()}
                                placeholder="Select a timezone"
                                searchPlaceholder="Search timezones"
                                modalTitle="Select Time Zone"
                              />
                          </div>
                        </div>
                        {/* Action Buttons */}
                        <div className="action-buttons">
                          <button onClick={() => setAqiStandardExpanded(false)} className="secondary-button">Cancel</button>
                          <button
                            onClick={handleSaveAllPreferences}
                            disabled={isSaving}
                            className="primary-button"
                          >
                            {isSaving && (
                              <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
                            )}
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Demo Account Visibility Section — hidden per product
                      decision: demo device is auto-shown when the user has 0
                      real devices, so the toggle is no longer needed.
                      Uncomment the block below to restore the setting. */}
                  {/*
                  <div className="card">
                    <div
                      className="card-header"
                      onClick={() => {
                        setDemoAccountExpanded(!demoAccountExpanded);
                        aqiStandardExpanded && setAqiStandardExpanded(false);
                        interfaceExpanded && setInterfaceExpanded(false);
                        unitPreferencesExpanded && setUnitPreferencesExpanded(false);
                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/interfaceIcon.svg`}
                            alt="Demo Account"
                          />
                        </div>
                        <div>
                          <h3 className="card-title">Demo Account</h3>
                          <p className="card-subtitle">
                            Choose whether to see the sample demo device. When off, alerts and share links cannot be created until you onboard a device.
                          </p>
                        </div>
                      </div>
                      {demoAccountExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {demoAccountExpanded && (
                      <div className="card-body">
                        <div className="grid grid-cols-2 gap-4 max-w-5xl">
                          <div className="select-form-field">
                            <label className="register-field-label settings-field-label">
                              Show demo account
                            </label>
                            <div className="register-input-with-icon">
                              <select
                                value={showDemoAccount ? 'yes' : 'no'}
                                onChange={e => setShowDemoAccount(e.target.value === 'yes')}
                                className="select-input"
                              >
                                <option value="yes">Yes, show demo account</option>
                                <option value="no">No, hide demo account</option>
                              </select>
                              <ChevronDown className="w-6 h-6 select-input-icon text-[var(--color-text)]" />
                            </div>
                          </div>
                        </div>

                        // Action Buttons
                        <div className="action-buttons">
                          <button onClick={() => setDemoAccountExpanded(false)} className="secondary-button">Cancel</button>
                          <button
                            onClick={handleSaveDemoAccountPreference}
                            disabled={isSaving}
                            className="primary-button"
                          >
                            {isSaving && (
                              <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
                            )}
                            <span>{isSaving ? 'Saving...' : 'Save'}</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  */}

                  {/* Unit Preferences Section */}
                  <div className="card">
                    <div
                      className="card-header"
                      onClick={() => {
                        setUnitPreferencesExpanded(!unitPreferencesExpanded)
                        aqiStandardExpanded && setAqiStandardExpanded(false);
                        interfaceExpanded && setInterfaceExpanded(false);
                        demoAccountExpanded && setDemoAccountExpanded(false);

                      }}
                    >
                      <div className="card-header-content">
                        <div className="card-icon">
                          <img
                            src={`${process.env.PUBLIC_URL}/SettingsPageIcons/UnitsIcon.svg`}
                            alt="Unit Preferences"
                          />
                        </div>
                        <div>
                          <h3 className="card-title">Unit Preferences</h3>
                          <p className="card-subtitle">
                            Set your preferred units for the parameters
                          </p>
                        </div>
                      </div>
                      {unitPreferencesExpanded ? (
                        <ChevronUp className="card-chevron" />
                      ) : (
                        <ChevronDown className="card-chevron" />
                      )}
                    </div>

                    {unitPreferencesExpanded && (
                      <div className="card-body">
                        <div className="grid grid-cols-2 gap-4 max-w-5xl">
                          {allParameters.length > 0 ? (
                            allParameters.map(
                              (parameter) => {
                                // Special handling for Temperature - use radio buttons
                                if (parameter.parameterName === 'Temperature') {
                                  return (
                                    <div key={parameter.parameterId} className="select-form-field">
                                      <label className="register-field-label settings-field-label">
                                        {parameter.parameterName}
                                      </label>
                                      <div className="temperature-radio-group">
                                        {getUnitOptions(parameter.parameterName).map(unit => (
                                          <label key={unit} className="temperature-radio-option">
                                            <input
                                              type="radio"
                                              name={`temperature-${parameter.parameterId}`}
                                              value={unit}
                                              checked={(parameter.preferredUnitName || '°C') === unit}
                                              onChange={_e => {
                                                const syntheticEvent = {
                                                  target: { value: unit }
                                                } as React.ChangeEvent<HTMLSelectElement>;
                                                handleParameterUnitChange(syntheticEvent, parameter.parameterId);
                                              }}
                                              className="temperature-radio-input"
                                            />
                                            <span className="temperature-radio-label">
                                              {unit === '°C' ? '°C' : unit === '°F' ? '°F' : unit}
                                            </span>
                                          </label>
                                        ))}
                                      </div>
                                    </div>
                                  );
                                }
                                
                                // For other parameters, use dropdown
                                return (
                                  <div key={parameter.parameterId} className="select-form-field">
                                    <label className="register-field-label settings-field-label">
                                      {parameter.parameterName}
                                    </label>
                                    <div className="register-input-with-icon">
                                      <select
                                        value={parameter.preferredUnitName || ''}
                                        onChange={e => handleParameterUnitChange(e, parameter.parameterId)}
                                        className="select-input"
                                      >
                                        {getUnitOptions(parameter.parameterName).map(unit => (
                                          <option key={unit} value={unit}>
                                            {unit === '°C' ? 'Temperature (°C)' : unit === '°F' ? 'Temperature (°F)' : unit}
                                          </option>
                                        ))}
                                      </select>
                                      <ChevronDown className="w-6 h-6 select-input-icon text-[var(--color-text)]" />
                                    </div>
                                  </div>
                                );
                              }
                            )
                            ) : (
                              <div className="Parameter-Preferences-Empty-State">
                                <p className="Parameter-Preferences-Empty-State-Description">
                                  You haven&apos;t onboarded a device yet.{' '}
                                  <button
                                    type="button"
                                    onClick={() => {
                                      showBottomBar();
                                      navigate('/add-device');
                                    }}
                                    className="text-[var(--color-terms-link)] underline hover:opacity-80"
                                  >
                                    Onboard your first device
                                  </button>{' '}
                                  to set unit preferences.
                                </p>
                              </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        {allParameters.length > 0 && 
                          <div className="action-buttons">
                            <button onClick={() => setUnitPreferencesExpanded(false)} className="secondary-button">Cancel</button>
                            <button
                              onClick={handleSaveAllPreferences}
                              disabled={isSaving}
                              className="primary-button"
                            >
                              {isSaving && (
                                <div className="h-6 w-6 border-b-2 border-white animate-spin rounded-full"></div>
                              )}
                              <span>{isSaving ? 'Saving...' : 'Save'}</span>
                            </button>
                          </div>
                        }
                      </div>
                    )}
                  </div>

                </div>
              )}

              {settingsActiveTab === 'api' && (
                <div className="tab-content">
                  {/* Header */}
                  <div className="settings-header">
                    <h2 className="settings-title">
                      API Keys{' '}
                      <p className="username-badge">{username || 'N/A'}</p>
                    </h2>
                    <p className="settings-subtitle">
                      Add or update your API key to enable external service
                      integrations.
                    </p>
                  </div>

                  {/* API Key Management Card */}
                  <div className="card">
                    <div className="api-key-card-body">
                      <div className="form-field">
                        <label className="register-field-label settings-field-label">
                          Shareable URL
                        </label>
                        <div className="api-key-container">
                          <input
                            type="text"
                            value={apiKey}
                            readOnly
                            className="api-key-input"
                          />
                          <button
                            onClick={handleCopyApiKey}
                            title="Copy to clipboard"
                            className="copy-button"
                          >
                            <Copy className="h-5 w-5 text-[var(--color-terms-link)]" />
                          </button>
                        </div>
                      </div>

                      <div className="api-actions">
                        <button
                          onClick={generateNewApiKey}
                          className="primary-button"
                        >
                          Generate New Key
                        </button>
                        <button className="danger-button">Delete</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Mobile Screens - Only visible on mobile devices */}
          {!isLoading && (
            <div className="mobile-screens-container">
              {mobileScreen === 'profile' && (
                <MobileProfileScreen
                  profilePictureUrl={profilePictureUrl}
                  firstName={firstName}
                  phoneNumber={e164ToBackendFormat(phoneNumber)}
                  email={email}
                  setMobileScreen={setMobileScreen}
                  setShowLogoutModal={setShowLogoutModal}
                  hideBottomBar={hideBottomBar}
                  theme={theme}
                />
              )}
              {mobileScreen === 'account' && (
                <MobileAccountScreen
                  profilePictureUrl={profilePictureUrl}
                  handleUploadClick={handleUploadClick}
                  handleDeletePhoto={handleDeletePhoto}
                  isUploading={isUploading}
                  isDeletingPhoto={isDeletingPhoto}
                  accountFields={accountFields}
                  handleSavePersonalInfo={handleSavePersonalInfo}
                  resetPersonalInfo={resetPersonalInfo}
                  isSaving={isSaving}
                  setMobileScreen={setMobileScreen}
                  fieldErrors={fieldErrors}
                  setFieldErrors={setFieldErrors}
                  setShowDeleteAccountModal={setShowDeleteAccountModal}
                  theme={theme}
                  openChangeEmailModal={openChangeEmailModal}
                />
              )}
              {mobileScreen === 'password' && (
                <MobilePasswordScreen
                  passwordFields={passwordFields}
                  newPassword={newPassword}
                  confirmPassword={confirmPassword}
                  passwordError={passwordError}
                  setPasswordError={setPasswordError}
                  isUpdatingPassword={isUpdatingPassword}
                  isPasswordFormValid={isPasswordFormValid}
                  handleUpdatePassword={handleUpdatePassword}
                  setMobileScreen={setMobileScreen}
                  setNewPassword={setNewPassword}
                  setConfirmPassword={setConfirmPassword}
                  hasPassword={!!currentUser?.hasPassword}
                />
              )}
              {mobileScreen === 'preferences' && (
                <MobilePreferencesScreen
                  aqiStandardId={aqiStandardId}
                  setAqiStandardId={setAqiStandardId}
                  aqiStandards={aqiStandards}
                  timezone={timezone}
                  setTimezone={setTimezone}
                  allParameters={allParameters}
                  handleParameterUnitChange={handleParameterUnitChange}
                  getUnitOptions={getUnitOptions}
                  handleSavePreferences={handleSaveAllPreferences}
                  isSaving={isSaving}
                  setMobileScreen={setMobileScreen}
                  // showDemoAccount={showDemoAccount}
                  // setShowDemoAccount={setShowDemoAccount}
                />
                )}
                {mobileScreen === 'interface' && (
                  <MobileInterfaceScreen
                    toggleTheme={toggleTheme}
                    setMobileScreen={setMobileScreen}
                    preferredTheme={preferredTheme}
                    setPreferredTheme={setPreferredTheme}
                  />
                )}
            </div>
          )}
        </div>

        {/* Change Email Modal — rendered inside layout so mobile (position: relative) overlays the screen */}
        <ChangeEmailModal
          isOpen={showChangeEmailModal}
          onClose={() => setShowChangeEmailModal(false)}
          currentEmail={email}
          onSubmit={handleChangeEmailSubmit}
        />
      </DashboardLayout>

      {/* Logout Confirmation Modal */ }
      <LogoutConfirmationModal
        isOpen={showLogoutModal}
        onClose={handleLogoutCancel}
        onConfirm={handleLogoutConfirm}
        isLoading={isLoggingOut}
      />

      {/* Account Deletion Modal */}
      <AccountDeletionModal
        isOpen={showDeleteAccountModal}
        onClose={handleDeleteAccountCancel}
        onConfirm={handleDeleteAccount}
        isLoading={isDeletingAccount}
        errorMessage={deleteAccountError}
        requirePassword={!!currentUser?.hasPassword}
      />

  </>
  );
};

export default SettingsPage;
